// apps/client/src/components/AIChat/ChatMessage.tsx

import React from 'react';
import { User, Bot, RefreshCw } from 'lucide-react';
import { Message } from './types';
import { InsightCard } from './InsightCard';
import { VisualizationRenderer } from './VisualizationRenderer';

interface ChatMessageProps {
  message: Message;
  onRetry?: () => void;
}

/**
 * Individual chat message component
 * Displays user or AI messages with insights and visualizations
 */
export function ChatMessage({ message, onRetry }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isError = message.error;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}>
      {/* Avatar (AI only, on left) */}
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 bg-opacity-20 flex items-center justify-center">
          <Bot className="w-5 h-5 text-orange-400" />
        </div>
      )}

      {/* Message bubble */}
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        {/* Message content */}
        <div
          className={`rounded-lg px-4 py-2 ${
            isUser
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
              : isError
              ? 'bg-red-900/50 border border-red-500 text-red-200'
              : 'bg-gray-700/50 backdrop-blur-sm border border-gray-600 text-gray-100'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>

          {/* Error retry button - prominent for timeout/errors */}
          {isError && onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Question
            </button>
          )}
        </div>

        {/* Insights (AI messages only) */}
        {!isUser && message.insights && message.insights.length > 0 && (
          <div className="mt-2 space-y-2 w-full">
            {message.insights.map((insight, i) => (
              <InsightCard key={i} insight={insight} />
            ))}
          </div>
        )}

        {/* Visualizations (AI messages only) */}
        {!isUser && message.visualizations && message.visualizations.length > 0 && (
          <div className="mt-2 w-full space-y-2">
            {message.visualizations.map((viz, i) => (
              <VisualizationRenderer key={i} visualization={viz} />
            ))}
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-gray-400 mt-1">
          {message.timestamp.toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>

      {/* Avatar (User only, on right) */}
      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center">
          <User className="w-5 h-5 text-gray-300" />
        </div>
      )}
    </div>
  );
}
